import { IntlService } from "@progress/kendo-react-intl";

const intl = new IntlService("en");

export const flattenExportColumns = (columns = []) => {
  const flattened = [];

  columns.forEach((column) => {
    if (!column.show || column.title === "") {
      return;
    }

    if (column.childCols?.length) {
      column.childCols
        .filter((childColumn) => childColumn.show && childColumn.title !== "")
        .forEach((childColumn) => flattened.push(childColumn));
      return;
    }

    flattened.push(column);
  });

  return flattened;
};

export const formatExportNumber = (fieldVal, format) => {
  if (fieldVal === null || fieldVal === undefined || fieldVal === "") {
    return "";
  }

  if (!format) {
    return fieldVal;
  }

  const numberVal = intl.parseNumber(fieldVal || 0);
  return intl.format(format, numberVal);
};

const getFormatDecimals = (stripped) => {
  const section = stripped.split(";")[0];
  const kendoMatch = section.match(/^[a-zA-Z](\d+)?$/);
  if (kendoMatch) {
    return kendoMatch[1] !== undefined ? Number(kendoMatch[1]) : 2;
  }
  const dot = section.indexOf(".");
  if (dot === -1) {
    return 0;
  }
  return section.slice(dot + 1).replace(/[^0#]/g, "").length;
};

const getValueDecimals = (val) => {
  if (val === null || val === undefined || val === "") {
    return 0;
  }
  const num = Number(val);
  if (isNaN(num)) {
    return 0;
  }
  const str = String(num).trim();
  // Small/large numbers stringify to exponential form (e.g. "1e-7", "1.5e-7") which has
  // no ".", so derive the decimal count from the mantissa fraction and exponent instead.
  const sci = str.match(/^-?\d*\.?(\d*)e([+-]?\d+)$/i);
  if (sci) {
    const fractionLen = sci[1] ? sci[1].length : 0;
    const exponent = parseInt(sci[2], 10);
    return Math.max(0, fractionLen - exponent);
  }
  const dot = str.indexOf(".");
  return dot === -1 ? 0 : str.length - dot - 1;
};

// Decides the Excel format for a numeric column so the export matches the grid.
// - A column whose configured format already shows enough decimals is left as-is.
// - A column with no format (or too few decimals) would be rounded by Excel, so we
//   apply an explicit decimal format that preserves the data's actual precision
//   (e.g. Pay Rate 69.85, whose metadata format is empty).
export const getExportCellFormat = (column, data) => {
  if (!column || column.type !== "number") {
    return undefined;
  }

  const rawFormat = typeof column.format === "string" ? column.format : "";
  const stripped = rawFormat.replace("{0:", "").replace("}", "");
  const formatDecimals = stripped ? getFormatDecimals(stripped) : 0;

  const maxDataDecimals = (data || []).reduce((max, row) => {
    const decimals = getValueDecimals(row ? row[column.field] : undefined);
    return decimals > max ? decimals : max;
  }, 0);

  if (stripped && formatDecimals >= maxDataDecimals) {
    return stripped;
  }

  if (maxDataDecimals > 0) {
    return "0." + "#".repeat(maxDataDecimals);
  }

  return stripped || undefined;
};

// Returns a copy of the summary data with boolean columns rendered as "true"/"false"
// (null/empty/0 all render as "false"), matching the grid so the export doesn't show raw 0/1.
export const getExportSummaryData = (columns, data) => {
  const booleanFields = (columns || [])
    .filter((column) => column && column.type === "boolean" && column.field)
    .map((column) => column.field);

  if (!booleanFields.length) {
    return data || [];
  }

  return (data || []).map((row) => {
    if (!row) {
      return row;
    }

    const next = { ...row };
    booleanFields.forEach((field) => {
      next[field] = Number(next[field]) ? "true" : "false";
    });

    return next;
  });
};

export const getKpiSummaryFooterValue = (data, column) => {
  // The footer render must never return undefined (Kendo/React throws "Nothing was
  // returned from render"); non-total columns render an empty footer cell instead.
  if (column.type !== "number" || !column.footerTemplate) {
    return "";
  }

  const total = (data || []).reduce((acc, item) => acc + (Number(item[column.field]) || 0), 0);

  if (!total) {
    return "";
  }

  return formatExportNumber(total, column.format);
};

export const applyKpiSummarySheetFormatting = (sheet, data, columns) => {
  if (!sheet?.rows) {
    return sheet;
  }

  sheet.rows.forEach((row) => {
    if (row.type === "data") {
      row.cells?.forEach((cell, index) => {
        const column = columns[index];
        if (column?.type === "number" && column.format && !isNaN(Number(cell.value))) {
          cell.value = formatExportNumber(cell.value, column.format);
        }
      });
    } else if (row.type === "footer") {
      row.cells?.forEach((cell) => {
        cell.background = "#7AC143";
      });
    }
  });

  return sheet;
};
